import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, ShoppingBag, Plus, Star, ChevronLeft, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;
const PLAN_RING = {
  OURO: "border-amber-400 ring-1 ring-amber-300",
  PRATA: "border-stone-300",
  BRONZE: "border-orange-300",
  GRATIS: "border-stone-200",
};

export default function Shop() {
  const { user } = useAuth();
  const { addItem } = useCart() || {};
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, product_categories: [], company_categories: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", category: "all", company_category: "all", city_slug: "all", kind: "all" });
  const [cities, setCities] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, page_size: 24 });
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
    api.get(`/products/shop?${p}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const onFilter = (key, val) => { setPage(1); setFilters({...filters, [key]: val}); };

  const addToCart = (p) => {
    if (!user) { toast.info("Faça login para comprar"); nav("/login"); return; }
    if (user.role !== "CUSTOMER") { toast.error("Apenas clientes podem comprar"); return; }
    addItem(p.company.id, p.company.name, {
      product_id: p.id, title: p.title, unit_price: p.price,
      image_url: p.image_url, cashback_pct_on_checkout: p.cashback_pct_on_checkout || 0,
      turbo_cashback_pct_on_checkout: p.turbo_active ? (p.turbo_cashback_pct || 0) : 0,
    });
    toast.success(`${p.title} adicionado ao carrinho!`);
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
      <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div>
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-amber-700 font-bold flex items-center gap-1.5"><Sparkles size={12}/> Shop</span>
          <h1 className="font-display text-2xl sm:text-4xl font-bold text-stone-900 mt-1 flex items-center gap-2"><ShoppingBag size={22} className="sm:hidden"/><ShoppingBag size={28} className="hidden sm:inline"/> Tudo num só lugar</h1>
          <p className="text-stone-500 text-xs sm:text-sm mt-0.5 sm:mt-1">Produtos e serviços de todas as lojas — empresas OURO com prioridade.</p>
        </div>
      </div>

      <Card className="p-3 sm:p-4 rounded-2xl border-stone-200 mb-4 sm:mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 sm:gap-3 items-end">
          <div className="col-span-2 sm:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input value={filters.q} onChange={(e) => setFilters({...filters, q: e.target.value})} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar produto/serviço..." className="rounded-xl pl-8 h-10 text-sm" data-testid="shop-search"/>
          </div>
          <div>
            <Select value={filters.kind} onValueChange={(v) => onFilter("kind", v)}>
              <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="shop-kind"><SelectValue placeholder="Tipo"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Tudo</SelectItem><SelectItem value="product">Produtos</SelectItem><SelectItem value="service">Serviços</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Select value={filters.company_category} onValueChange={(v) => onFilter("company_category", v)}>
              <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="shop-comp-cat"><SelectValue placeholder="Empresa"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas empresas</SelectItem>
                {data.company_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select value={filters.category} onValueChange={(v) => onFilter("category", v)}>
              <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="shop-prod-cat"><SelectValue placeholder="Categoria"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas categorias</SelectItem>
                {data.product_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select value={filters.city_slug} onValueChange={(v) => onFilter("city_slug", v)}>
              <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="shop-city"><SelectValue placeholder="Cidade"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas cidades</SelectItem>
                {cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="py-20 text-center text-stone-400"><Loader2 className="mx-auto animate-spin" size={40}/></div>
      ) : data.items.length === 0 ? (
        <Card className="p-8 sm:p-12 rounded-2xl border-stone-200 text-center" data-testid="shop-empty">
          <ShoppingBag className="mx-auto text-stone-300" size={48}/>
          <p className="font-display text-xl sm:text-2xl mt-3 sm:mt-4">Nada encontrado</p>
          <p className="text-stone-500 text-xs sm:text-sm mt-1">Ajuste os filtros e tente de novo.</p>
        </Card>
      ) : (
        <>
          <p className="text-xs text-stone-500 mb-2 sm:mb-3">{data.total} resultado(s)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4">
            {data.items.map((p) => {
              const plan = p.company?.plan || "GRATIS";
              const isOuro = plan === "OURO";
              return (
                <Link key={p.id} to={`/product/${p.id}`} className={`group rounded-xl sm:rounded-2xl bg-white border ${PLAN_RING[plan] || PLAN_RING.GRATIS} hover:-translate-y-1 hover:shadow-xl transition-all overflow-hidden relative block`} data-testid={`shop-card-${p.id}`}>
                  {isOuro && <span className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[9px] font-bold uppercase tracking-wider shadow"><Star size={8} className="fill-current"/>Top</span>}
                  <div className="aspect-square bg-stone-100 overflow-hidden relative">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300"><ShoppingBag size={32}/></div>
                    )}
                    {p.cashback_pct_on_checkout > 0 && (
                      <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-100/95 text-emerald-900 text-[9px] font-bold backdrop-blur-sm">+{p.cashback_pct_on_checkout}%</span>
                    )}
                  </div>
                  <div className="p-2 sm:p-2.5">
                    <h3 className="font-display font-semibold text-stone-900 leading-tight text-xs sm:text-sm line-clamp-2 min-h-[2.25rem]">{p.title}</h3>
                    <div className="text-[10px] text-stone-500 mt-0.5 line-clamp-1">{p.company?.name}</div>
                    <div className="flex items-center justify-between mt-1.5 gap-1">
                      {p.price > 0 ? <span className="font-display text-sm sm:text-base font-bold text-blue-950">{fmt(p.price)}</span> : <span className="text-[10px] text-stone-400">Sob consulta</span>}
                      {p.checkout_enabled && p.price > 0 ? (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToCart(p); }} className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-900 text-white hover:bg-blue-950 shrink-0" data-testid={`shop-add-${p.id}`}>
                          <Plus size={12}/>
                        </button>
                      ) : null}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {data.pages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-6 sm:mt-8">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg" data-testid="shop-prev"><ChevronLeft size={14}/></Button>
              <span className="text-xs sm:text-sm text-stone-600">Página {data.page} de {data.pages}</span>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-lg" data-testid="shop-next"><ChevronRight size={14}/></Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
